/**
 * A per-browser opt-out of the revamp, for a tenant that has it.
 *
 * `studio-tree-insights` says a tenant SHOULD get the new experience; this says
 * this person, in this browser, wants today's one back. So it is read inside
 * `fetchTreeInsightsEnabled` — the one helper every consumer of that flag goes
 * through — and turns a `true` answer into `false`. It can only decline the
 * revamp, never grant it: granting is LaunchDarkly's job.
 *
 * Never read during render. The app is server-rendered and hydrated, and
 * storage does not exist on the server, so a render that reads it cannot match
 * the server's markup. Resolve it after mount instead.
 *
 * Every access is wrapped: a private window, blocked site data or SSR must all
 * read as "not forced", which is the product as shipped.
 */

const keyFor = (tenant: string) => `studio-legacy:${tenant}`;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage ?? null;
  } catch {
    return null;
  }
}

export function legacyForced(tenant: string): boolean {
  try {
    return storage()?.getItem(keyFor(tenant)) === 'true';
  } catch {
    return false;
  }
}

/** Off removes the key, so "never chose" and "chose the new one" are one state. */
export function setLegacyForced(tenant: string, on: boolean): void {
  try {
    const store = storage();
    if (on) store?.setItem(keyFor(tenant), 'true');
    else store?.removeItem(keyFor(tenant));
  } catch {
    // A browser that refuses the write leaves the person where they were.
  }
}
